import { useState } from 'react';
import { Truck, Mail, Wallet } from 'lucide-react';
import { useWalletDelivery, useEmailDelivery } from '../hooks/useApi';
import { DataTable } from '../components/shared/DataTable';
import { MetricCard } from '../components/shared/MetricCard';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import type { WalletDeliveryByOrganisation } from '../types/api';

export function DeliveryPage() {
  const [emailDays, setEmailDays] = useState(7);
  const wallet = useWalletDelivery();
  const email = useEmailDelivery(emailDays);

  const walletColumns = [
    { key: 'organisationName', header: 'Organizacja', sortable: true },
    { key: 'totalIssued', header: 'Wystawione', sortable: true },
    {
      key: 'walletDelivered',
      header: 'Dostarczone',
      sortable: true,
      render: (row: WalletDeliveryByOrganisation) => <span className="text-emerald-600 dark:text-emerald-300">{row.walletDelivered}</span>,
    },
    {
      key: 'walletFailed',
      header: 'Bledy',
      sortable: true,
      render: (row: WalletDeliveryByOrganisation) =>
        row.walletFailed > 0 ? <span className="text-red-600 dark:text-red-300 font-medium">{row.walletFailed}</span> : <span className="text-gray-400 dark:text-gray-600">0</span>,
    },
    {
      key: 'walletPending',
      header: 'Oczekujace',
      sortable: true,
      render: (row: WalletDeliveryByOrganisation) =>
        row.walletPending > 0 ? <span className="text-amber-600 dark:text-amber-300">{row.walletPending}</span> : <span className="text-gray-400 dark:text-gray-600">0</span>,
    },
    {
      key: 'walletDeliveryPercent',
      header: '% dostarczenia',
      sortable: true,
      render: (row: WalletDeliveryByOrganisation) => {
        const pct = row.walletDeliveryPercent;
        const color = pct >= 95 ? 'text-emerald-600 dark:text-emerald-300' : pct >= 80 ? 'text-amber-600 dark:text-amber-300' : 'text-red-600 dark:text-red-300';
        return <span className={`font-semibold ${color}`}>{pct.toFixed(1)}%</span>;
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Truck className="h-6 w-6 text-brand-600 dark:text-brand-400" />
          Dostarczalnosc
        </h1>
        <p className="text-sm text-gray-500 mt-1">Status dostarczenia do Wallet i emaili</p>
      </div>

      {/* Email summary */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Mail className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            Dostarczalnosc emaili
          </h2>
          <select value={emailDays} onChange={(e) => setEmailDays(Number(e.target.value))} className="input-field text-sm">
            <option value={1}>Ostatni dzien</option>
            <option value={7}>Ostatnie 7 dni</option>
            <option value={30}>Ostatnie 30 dni</option>
          </select>
        </div>
        {email.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : email.error ? (
          <ErrorAlert message={email.error.message} onRetry={() => email.refetch()} />
        ) : email.data ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <MetricCard title="Razem" value={email.data.totalEmails} />
            <MetricCard title="Wyslane" value={email.data.sent} variant="success" />
            <MetricCard title="Dostarczone" value={email.data.delivered} variant="success" />
            <MetricCard title="Bounce" value={email.data.bounced} variant={email.data.bounced > 0 ? 'warning' : 'default'} />
            <MetricCard title="Bledy" value={email.data.failed} variant={email.data.failed > 0 ? 'danger' : 'default'} />
          </div>
        ) : null}
      </div>

      {/* Wallet delivery */}
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-4">
          <Wallet className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          Dostarczenie do Wallet per organizacja
        </h2>
        {wallet.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : wallet.error ? (
          <ErrorAlert message={wallet.error.message} onRetry={() => wallet.refetch()} />
        ) : wallet.data ? (
          <DataTable columns={walletColumns} data={wallet.data} compact />
        ) : null}
      </div>
    </div>
  );
}
